import { canReadAttachment, findAttachmentByName } from '../../selectors';
import type { Attachment, AttachmentId, MockCache, TextureLink } from '../../types';

import { bump, requireCache } from '../runtime';

export type TextureSource = 'manual' | 'found' | 'missing';

const MAX_TEXTURE_NAME_LENGTH = 255;
const FORBIDDEN_NAME_CHARACTERS = /[/\\?#]/;

function texturesKey(modelAttachmentId: AttachmentId): string {
  return `textures:${String(modelAttachmentId)}`;
}

function linkKey(modelAttachmentId: AttachmentId, name: string): string {
  return `${String(modelAttachmentId)}:${name}`;
}

function assertTextureName(name: string): void {
  const valid =
    name.length > 0 &&
    name.length <= MAX_TEXTURE_NAME_LENGTH &&
    name === name.toLowerCase() &&
    !FORBIDDEN_NAME_CHARACTERS.test(name);
  if (!valid) throw new Error('Неверное имя текстуры');
}

function readableModel(cache: MockCache, modelAttachmentId: AttachmentId): Attachment {
  const model = cache.attachments.get(modelAttachmentId);
  if (model === undefined || !canReadAttachment(cache, model)) throw new Error('Модель недоступна');
  return model;
}

function assertReadableTexture(cache: MockCache, model: Attachment, textureAttachmentId: AttachmentId): void {
  const texture = cache.attachments.get(textureAttachmentId);
  if (texture === undefined || texture.id === model.id || texture.kind !== 'image' || !canReadAttachment(cache, texture)) {
    throw new Error('Текстура недоступна');
  }
}

export function setTextureLink(args: { modelAttachmentId: AttachmentId; name: string; textureAttachmentId: AttachmentId }): void {
  const cache = requireCache();
  assertTextureName(args.name);
  const model = readableModel(cache, args.modelAttachmentId);
  assertReadableTexture(cache, model, args.textureAttachmentId);
  cache.textureLinks.set(linkKey(model.id, args.name), {
    modelAttachmentId: model.id,
    name: args.name,
    textureAttachmentId: args.textureAttachmentId,
  });
  bump(texturesKey(model.id));
}

export function clearTextureLink(args: { modelAttachmentId: AttachmentId; name: string }): void {
  const cache = requireCache();
  readableModel(cache, args.modelAttachmentId);
  if (cache.textureLinks.delete(linkKey(args.modelAttachmentId, args.name))) bump(texturesKey(args.modelAttachmentId));
}

export function selectTextureLinks(cache: MockCache, modelAttachmentId: AttachmentId): TextureLink[] {
  return [...cache.textureLinks.values()]
    .filter((link) => link.modelAttachmentId === modelAttachmentId)
    .sort((a, b) => a.name.localeCompare(b.name));
}

function linkedTexture(cache: MockCache, model: Attachment, name: string): Attachment | null {
  const link = cache.textureLinks.get(linkKey(model.id, name));
  const texture = link === undefined ? undefined : cache.attachments.get(link.textureAttachmentId);
  return texture !== undefined && canReadAttachment(cache, texture) ? texture : null;
}

export function resolveModelFile(cache: MockCache, model: Attachment, name: string): Attachment | null {
  return linkedTexture(cache, model, name) ?? findAttachmentByName(cache, name, model);
}

export function textureSource(cache: MockCache, model: Attachment, name: string): TextureSource {
  if (linkedTexture(cache, model, name) !== null) return 'manual';
  return findAttachmentByName(cache, name, model) === null ? 'missing' : 'found';
}

export function selectReadableImages(cache: MockCache): Attachment[] {
  return [...cache.attachments.values()]
    .filter((attachment) => attachment.kind === 'image' && canReadAttachment(cache, attachment))
    .sort((a, b) => Number(b.createdAt) - Number(a.createdAt) || String(b.id).localeCompare(String(a.id)));
}

export function removeTextureLinksOf(cache: MockCache, attachmentId: AttachmentId): void {
  for (const [key, link] of cache.textureLinks) {
    if (link.modelAttachmentId === attachmentId || link.textureAttachmentId === attachmentId) {
      cache.textureLinks.delete(key);
      bump(texturesKey(link.modelAttachmentId));
    }
  }
}
